import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PricingPage = () => {
  const plans = [
    {
      name: 'Básico',
      price: 'R$29',
      description: 'Perfeito para pequenas equipes começando',
      features: [
        'Até 5 membros da equipe',
        'Rastreamento básico de metas',
        'Gestão de clientes',
        'Suporte por email',
        '10 GB de armazenamento',
        'Análises básicas'
      ],
      popular: false
    },
    {
      name: 'Profissional',
      price: 'R$79',
      description: 'Para empresas em crescimento que precisam de mais',
      features: [
        'Até 25 membros da equipe',
        'Rastreamento avançado de metas',
        'Gestão completa de clientes',
        'Suporte prioritário',
        '100 GB de armazenamento',
        'Análises avançadas',
        'Integrações personalizadas',
        'Rastreamento de implementação'
      ],
      popular: true
    },
    {
      name: 'Empresarial',
      price: 'Personalizado',
      description: 'Para grandes organizações com necessidades específicas',
      features: [
        'Membros ilimitados',
        'Rastreamento empresarial de metas',
        'Gestão avançada de clientes',
        'Suporte dedicado 24/7',
        'Armazenamento ilimitado',
        'Análises personalizadas',
        'Acesso à API',
        'Segurança avançada',
        'Treinamento personalizado',
        'Garantias SLA'
      ],
      popular: false
    }
  ];

  return (
    <>
      <Helmet>
        <title>Preços - Dashboard Center</title>
        <meta name="description" content="Escolha o plano perfeito para sua equipe. Preços transparentes sem taxas ocultas." />
      </Helmet>

      <div className="min-h-screen bg-black">
        {/* Navigation */}
        <nav className="bg-white/5 backdrop-blur-lg border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center gap-2 text-white hover:text-orange-300 transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span>Voltar ao Início</span>
              </Link>
              <div className="flex items-center gap-4">
                <Link to="/login">
                  <Button variant="ghost" className="text-white hover:text-orange-300">
                    Entrar
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white">
                    Começar
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Pricing Header */}
        <section className="pt-20 pb-12 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Simples e Transparente
              </h1>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Escolha o plano que se adapta às suas necessidades. Todos os planos incluem um teste grátis de 14 dias.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pb-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`relative bg-white rounded-2xl p-8 border ${
                    plan.popular ? 'border-orange-500 shadow-2xl shadow-orange-500/20' : 'border-gray-200'
                  } hover:transform hover:scale-105 transition-all`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-orange-600 to-yellow-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Mais Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-black mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-4">{plan.description}</p>
                    <div className="mb-4">
                      <span className="text-5xl font-bold text-black">{plan.price}</span>
                      {plan.price !== 'Personalizado' && <span className="text-gray-500 ml-2">/mês</span>}
                    </div>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/register" className="block">
                    <Button
                      className={`w-full py-6 text-lg ${
                        plan.popular
                          ? 'bg-black hover:bg-orange-600 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-black border border-gray-200'
                      }`}
                    >
                      {plan.price === 'Personalizado' ? 'Contatar Vendas' : 'Testar Grátis'}
                    </Button>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Comparison */}
        <section className="pb-20 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border border-gray-200"
            >
              <h2 className="text-3xl font-bold text-black mb-8 text-center">Comparação de Recursos</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-4 px-4 text-black font-semibold">Recurso</th>
                      <th className="text-center py-4 px-4 text-black font-semibold">Básico</th>
                      <th className="text-center py-4 px-4 text-black font-semibold">Profissional</th>
                      <th className="text-center py-4 px-4 text-black font-semibold">Empresarial</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100">
                      <td className="py-4 px-4">Membros da Equipe</td>
                      <td className="text-center py-4 px-4">5</td>
                      <td className="text-center py-4 px-4">25</td>
                      <td className="text-center py-4 px-4">Ilimitado</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-4 px-4">Armazenamento</td>
                      <td className="text-center py-4 px-4">10 GB</td>
                      <td className="text-center py-4 px-4">100 GB</td>
                      <td className="text-center py-4 px-4">Ilimitado</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-4 px-4">Análises</td>
                      <td className="text-center py-4 px-4">Básico</td>
                      <td className="text-center py-4 px-4">Avançado</td>
                      <td className="text-center py-4 px-4">Personalizado</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-4 px-4">Suporte</td>
                      <td className="text-center py-4 px-4">Email</td>
                      <td className="text-center py-4 px-4">Prioritário</td>
                      <td className="text-center py-4 px-4">24/7 Dedicado</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4">Acesso à API</td>
                      <td className="text-center py-4 px-4">-</td>
                      <td className="text-center py-4 px-4">-</td>
                      <td className="text-center py-4 px-4"><Check className="w-5 h-5 text-orange-500 mx-auto" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PricingPage;